/**
 * Centralized Permission Registry for AirReserve
 *
 * This file defines all permissions and roles in the system with a hierarchical structure.
 * Benefits:
 * - Type safety with TypeScript constants
 * - Single source of truth for all permissions
 * - Clear inheritance relationships
 * - Easy to audit and maintain
 */

// =============================================================================
// PERMISSION CONSTANTS
// =============================================================================

/**
 * All available permissions in the system
 * Format: DOMAIN_ACTION where DOMAIN is the feature area and ACTION is the operation level
 */
export const PERMISSIONS = {
  // System-level permissions (highest level)
  SYSTEM_ADMIN: 'system:admin',
  SYSTEM_MANAGE: 'system:manage',

  // Global permissions (cross-domain administrative access)
  GLOBAL_ADMIN: 'global:admin',
  GLOBAL_READ: 'global:read',

  // Booking permissions
  BOOKINGS_READ: 'bookings:read',
  BOOKINGS_WRITE: 'bookings:write',
  BOOKINGS_ADMIN: 'bookings:admin',
  BOOKINGS_READ_OWN: 'bookings:read:own',
  BOOKINGS_WRITE_OWN: 'bookings:write:own',

  // Finance permissions
  FINANCES_READ: 'finances:read',
  FINANCES_WRITE: 'finances:write',
  FINANCES_ADMIN: 'finances:admin',
  FINANCES_READ_OWN: 'finances:read:own',

  // Aircraft permissions
  AIRCRAFT_READ: 'aircraft:read',
  AIRCRAFT_WRITE: 'aircraft:write',
  AIRCRAFT_ADMIN: 'aircraft:admin',
  AIRCRAFT_MAINTENANCE: 'aircraft:maintenance',

  // User management permissions
  USERS_READ: 'users:read',
  USERS_WRITE: 'users:write',
  USERS_ADMIN: 'users:admin',
  USERS_READ_OWN: 'users:read:own',
  USERS_WRITE_OWN: 'users:write:own',

  // Flight log permissions
  FLIGHTLOGS_READ: 'flightlogs:read',
  FLIGHTLOGS_WRITE: 'flightlogs:write',
  FLIGHTLOGS_ADMIN: 'flightlogs:admin',
  FLIGHTLOGS_READ_OWN: 'flightlogs:read:own',
  FLIGHTLOGS_WRITE_OWN: 'flightlogs:write:own',

  // Base user permissions (every authenticated user has these)
  PROFILE_READ: 'profile:read',
  PROFILE_WRITE: 'profile:write',
} as const;

// =============================================================================
// ROLE HIERARCHY DEFINITION
// =============================================================================

/**
 * Role hierarchy with inheritance relationships
 * Each role automatically inherits all permissions from roles listed in 'inherits'
 *
 * Key Design Principles:
 * 1. Higher-level roles inherit from lower-level roles
 * 2. Domain admins inherit all permissions within their domain
 * 3. Global admin inherits from all domain admins
 * 4. System admin has ultimate access
 */
export const ROLE_HIERARCHY = {
  // System owner (ultimate authority)
  [PERMISSIONS.SYSTEM_ADMIN]: {
    inherits: [PERMISSIONS.GLOBAL_ADMIN],
    permissions: [PERMISSIONS.SYSTEM_ADMIN, PERMISSIONS.SYSTEM_MANAGE],
  },

  // Global administrator (full access to all domains)
  [PERMISSIONS.GLOBAL_ADMIN]: {
    inherits: [
      PERMISSIONS.BOOKINGS_ADMIN,
      PERMISSIONS.FINANCES_ADMIN,
      PERMISSIONS.AIRCRAFT_ADMIN,
      PERMISSIONS.USERS_ADMIN,
      PERMISSIONS.FLIGHTLOGS_ADMIN,
    ],
    permissions: [PERMISSIONS.GLOBAL_ADMIN, PERMISSIONS.GLOBAL_READ],
  },

  // Domain administrators (full access within their domain)
  [PERMISSIONS.BOOKINGS_ADMIN]: {
    inherits: [PERMISSIONS.BOOKINGS_WRITE],
    permissions: [PERMISSIONS.BOOKINGS_ADMIN],
  },

  [PERMISSIONS.FINANCES_ADMIN]: {
    inherits: [PERMISSIONS.FINANCES_WRITE],
    permissions: [PERMISSIONS.FINANCES_ADMIN],
  },

  [PERMISSIONS.AIRCRAFT_ADMIN]: {
    inherits: [PERMISSIONS.AIRCRAFT_WRITE],
    permissions: [PERMISSIONS.AIRCRAFT_ADMIN, PERMISSIONS.AIRCRAFT_MAINTENANCE],
  },

  [PERMISSIONS.USERS_ADMIN]: {
    inherits: [PERMISSIONS.USERS_WRITE],
    permissions: [PERMISSIONS.USERS_ADMIN],
  },

  [PERMISSIONS.FLIGHTLOGS_ADMIN]: {
    inherits: [PERMISSIONS.FLIGHTLOGS_WRITE],
    permissions: [PERMISSIONS.FLIGHTLOGS_ADMIN],
  },

  // Domain write permissions (can modify data within domain)
  [PERMISSIONS.BOOKINGS_WRITE]: {
    inherits: [PERMISSIONS.BOOKINGS_READ],
    permissions: [PERMISSIONS.BOOKINGS_WRITE, PERMISSIONS.BOOKINGS_WRITE_OWN],
  },

  [PERMISSIONS.FINANCES_WRITE]: {
    inherits: [PERMISSIONS.FINANCES_READ],
    permissions: [PERMISSIONS.FINANCES_WRITE],
  },

  [PERMISSIONS.AIRCRAFT_WRITE]: {
    inherits: [PERMISSIONS.AIRCRAFT_READ],
    permissions: [PERMISSIONS.AIRCRAFT_WRITE],
  },

  [PERMISSIONS.USERS_WRITE]: {
    inherits: [PERMISSIONS.USERS_READ],
    permissions: [PERMISSIONS.USERS_WRITE, PERMISSIONS.USERS_WRITE_OWN],
  },

  [PERMISSIONS.FLIGHTLOGS_WRITE]: {
    inherits: [PERMISSIONS.FLIGHTLOGS_READ],
    permissions: [
      PERMISSIONS.FLIGHTLOGS_WRITE,
      PERMISSIONS.FLIGHTLOGS_WRITE_OWN,
    ],
  },

  // Domain read permissions (can view data within domain)
  [PERMISSIONS.BOOKINGS_READ]: {
    inherits: [],
    permissions: [
      PERMISSIONS.BOOKINGS_READ,
      PERMISSIONS.BOOKINGS_READ_OWN,
      PERMISSIONS.PROFILE_READ,
      PERMISSIONS.PROFILE_WRITE,
    ],
  },

  [PERMISSIONS.FINANCES_READ]: {
    inherits: [],
    permissions: [
      PERMISSIONS.FINANCES_READ,
      PERMISSIONS.FINANCES_READ_OWN,
      PERMISSIONS.PROFILE_READ,
      PERMISSIONS.PROFILE_WRITE,
    ],
  },

  [PERMISSIONS.AIRCRAFT_READ]: {
    inherits: [],
    permissions: [
      PERMISSIONS.AIRCRAFT_READ,
      PERMISSIONS.PROFILE_READ,
      PERMISSIONS.PROFILE_WRITE,
    ],
  },

  [PERMISSIONS.USERS_READ]: {
    inherits: [],
    permissions: [
      PERMISSIONS.USERS_READ,
      PERMISSIONS.USERS_READ_OWN,
      PERMISSIONS.PROFILE_READ,
      PERMISSIONS.PROFILE_WRITE,
    ],
  },

  [PERMISSIONS.FLIGHTLOGS_READ]: {
    inherits: [],
    permissions: [
      PERMISSIONS.FLIGHTLOGS_READ,
      PERMISSIONS.FLIGHTLOGS_READ_OWN,
      PERMISSIONS.PROFILE_READ,
      PERMISSIONS.PROFILE_WRITE,
    ],
  },
} as const;

// =============================================================================
// BACKWARD COMPATIBILITY MAPPING
// =============================================================================

/**
 * Maps existing scopes to new permission system
 * This allows gradual migration without breaking existing functionality
 */
export const LEGACY_SCOPE_MAPPING = {
  'global:admin': PERMISSIONS.GLOBAL_ADMIN,
  'read:bookings': PERMISSIONS.BOOKINGS_READ,
  'write:bookings': PERMISSIONS.BOOKINGS_WRITE,
  'read:finances': PERMISSIONS.FINANCES_READ,
  'write:finances': PERMISSIONS.FINANCES_WRITE,
} as const;

// =============================================================================
// TYPE DEFINITIONS
// =============================================================================

/**
 * Type definitions for better TypeScript support
 */
export type Permission = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];
export type Role = keyof typeof ROLE_HIERARCHY;
export type LegacyScope = keyof typeof LEGACY_SCOPE_MAPPING;

/**
 * Role configuration interface
 */
export interface RoleConfig {
  inherits: Permission[];
  permissions: Permission[];
}

/**
 * Helper type to get all available permissions
 */
export type AvailablePermissions = Permission;
